/**
 * Agenda builder — substitui o passo 2 do modal.
 *
 * Suporta dois tipos de agenda mutuamente exclusivos (discriminated union):
 *   - `cron`: expressão POSIX de 5 campos (recorrente)
 *   - `once`: timestamp futuro (one-shot, dispara 1 vez)
 *
 * Evitamos react-js-cron por exigir Ant Design como peer (peso e conflito
 * visual com o design system Tailwind). Para o modo cron, oferecemos 5
 * presets + modo avançado com input raw. Preview pt-BR via `cronstrue`
 * (recorrente) ou `Intl.DateTimeFormat` (one-shot).
 */

import { useEffect, useMemo, useState } from 'react'
import cronstrue from 'cronstrue/i18n'

import { Icon } from '@/design-system/atoms/Icon'
import { Input } from '@/design-system/atoms/Input'
import { Label } from '@/design-system/atoms/Label'
import { Alert } from '@/design-system/molecules/Alert'
import { FormField } from '@/design-system/molecules/FormField'
import { cn } from '@/lib/utils'

import type { Schedule } from '../../types/scheduler'

type CronMode =
  'daily' | 'weekdays' | 'custom_days' | 'monthly' | 'interval' | 'raw'

type Mode = CronMode | 'once'

interface Props {
  value: Schedule
  onChange: (schedule: Schedule) => void
  className?: string
}

const WEEKDAY_LABELS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
]

const INTERVAL_UNIT_OPTIONS = [
  { value: 'min', label: 'minutos' },
  { value: 'hour', label: 'horas' },
] as const

type IntervalUnit = (typeof INTERVAL_UNIT_OPTIONS)[number]['value']

/**
 * Identifica qual preset melhor casa com a cron atual para exibir no selector.
 * Quando nenhum preset bate, cai em "raw" (modo avançado).
 */
function detectCronMode(cron: string): CronMode {
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return 'raw'
  const [m, h, dom, month, dow] = parts

  if (month !== '*') return 'raw'

  if (m.startsWith('*/') && h === '*' && dom === '*' && dow === '*') {
    return 'interval'
  }
  if (m === '0' && h.startsWith('*/') && dom === '*' && dow === '*') {
    return 'interval'
  }

  if (!/^\d+$/.test(m) || !/^\d+$/.test(h)) return 'raw'

  if (dom === '*' && dow === '*') return 'daily'
  if (dom === '*' && dow === '1-5') return 'weekdays'
  if (dom === '*' && /^[\d,]+$/.test(dow)) return 'custom_days'
  if (dow === '*' && /^\d+$/.test(dom)) return 'monthly'

  return 'raw'
}

function parseTimeFromCron(cron: string): { hour: number; minute: number } {
  const parts = cron.trim().split(/\s+/)
  const m = Number(parts[0])
  const h = Number(parts[1])
  return {
    hour: Number.isFinite(h) ? h : 7,
    minute: Number.isFinite(m) ? m : 0,
  }
}

function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function parseTimeInput(value: string): { hour: number; minute: number } {
  const [h, m] = value.split(':').map(Number)
  return { hour: h || 0, minute: m || 0 }
}

function parseDaysFromCron(cron: string): Set<number> {
  const parts = cron.trim().split(/\s+/)
  const dow = parts[4] ?? ''
  if (!/^[\d,]+$/.test(dow)) return new Set([1, 2, 3, 4, 5])
  return new Set(dow.split(',').map(Number))
}

function parseDomFromCron(cron: string): number {
  const parts = cron.trim().split(/\s+/)
  const dom = Number(parts[2])
  return Number.isFinite(dom) && dom >= 1 && dom <= 31 ? dom : 1
}

function parseIntervalFromCron(cron: string): {
  every: number
  unit: IntervalUnit
} {
  const parts = cron.trim().split(/\s+/)
  const [m, h] = parts
  if (m.startsWith('*/') && h === '*') {
    return { every: Number(m.slice(2)) || 15, unit: 'min' }
  }
  if (m === '0' && h.startsWith('*/')) {
    return { every: Number(h.slice(2)) || 2, unit: 'hour' }
  }
  return { every: 15, unit: 'min' }
}

function buildCron(
  mode: CronMode,
  args: {
    hour: number
    minute: number
    days: Set<number>
    dom: number
    intervalEvery: number
    intervalUnit: IntervalUnit
    raw: string
  }
): string {
  const { hour, minute, days, dom, intervalEvery, intervalUnit, raw } = args
  switch (mode) {
    case 'daily':
      return `${minute} ${hour} * * *`
    case 'weekdays':
      return `${minute} ${hour} * * 1-5`
    case 'custom_days': {
      const sortedDays = [...days].sort((a, b) => a - b).join(',') || '*'
      return `${minute} ${hour} * * ${sortedDays}`
    }
    case 'monthly':
      return `${minute} ${hour} ${dom} * *`
    case 'interval':
      if (intervalUnit === 'min') return `*/${intervalEvery} * * * *`
      return `0 */${intervalEvery} * * *`
    case 'raw':
      return raw
  }
}

function humanizeCron(cron: string): string {
  try {
    return cronstrue.toString(cron, {
      locale: 'pt_BR',
      use24HourTimeFormat: true,
    })
  } catch {
    return 'Expressão cron inválida'
  }
}

function humanizeDate(iso: string): string {
  if (!iso) return 'Selecione data e hora'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return 'Data inválida'
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'full',
      timeStyle: 'short',
    }).format(d)
  } catch {
    return iso
  }
}

/** Default para modo "once": amanhã às 09:00 no fuso local. */
function defaultOnceRunAt(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return toDatetimeLocal(d)
}

/** Converte Date → string aceita por `<input type="datetime-local">`. */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

const MODE_OPTIONS: Array<{ value: Mode; label: string; hint: string }> = [
  { value: 'daily', label: 'Todo dia', hint: 'Uma execução por dia' },
  { value: 'weekdays', label: 'Dias úteis', hint: 'Segunda a sexta' },
  {
    value: 'custom_days',
    label: 'Dias específicos',
    hint: 'Escolha quais dias da semana',
  },
  { value: 'monthly', label: 'Mensal', hint: 'Um dia por mês' },
  { value: 'interval', label: 'Intervalo', hint: 'A cada N minutos ou horas' },
  { value: 'once', label: 'Data única', hint: 'Dispara 1 vez e encerra' },
  { value: 'raw', label: 'Avançado', hint: 'Cron POSIX direto' },
]

export default function ScheduleBuilder({ value, onChange, className }: Props) {
  const initialCron = value.kind === 'cron' ? value.expression : '30 7 * * 1-5'
  const initialRunAt =
    value.kind === 'once' && value.runAt
      ? toDatetimeLocal(new Date(value.runAt))
      : defaultOnceRunAt()

  const [mode, setMode] = useState<Mode>(
    value.kind === 'once' ? 'once' : detectCronMode(initialCron)
  )

  // Estado do modo cron
  const parsedCron = useMemo(
    () => parseTimeFromCron(initialCron),
    [initialCron]
  )
  const [hour, setHour] = useState(parsedCron.hour)
  const [minute, setMinute] = useState(parsedCron.minute)
  const [days, setDays] = useState<Set<number>>(() =>
    parseDaysFromCron(initialCron)
  )
  const [dom, setDom] = useState(() => parseDomFromCron(initialCron))
  const initialInterval = useMemo(
    () => parseIntervalFromCron(initialCron),
    [initialCron]
  )
  const [intervalEvery, setIntervalEvery] = useState(initialInterval.every)
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>(
    initialInterval.unit
  )
  const [raw, setRaw] = useState(initialCron)

  // Estado do modo once
  const [runAtInput, setRunAtInput] = useState(initialRunAt)

  // Reconstrói/emite o Schedule sempre que o modo ou campos visuais mudam.
  useEffect(() => {
    if (mode === 'once') {
      if (!runAtInput) return
      // datetime-local é "naive" — o browser entende no fuso local. ISO com
      // offset é calculado via Date(). Se inválido, o preview alerta.
      const d = new Date(runAtInput)
      if (Number.isNaN(d.getTime())) return
      onChange({ kind: 'once', runAt: d.toISOString() })
      return
    }
    if (mode === 'raw') {
      onChange({ kind: 'cron', expression: raw })
      return
    }
    const next = buildCron(mode, {
      hour,
      minute,
      days,
      dom,
      intervalEvery,
      intervalUnit,
      raw,
    })
    setRaw(next)
    onChange({ kind: 'cron', expression: next })
    // `onChange` intencionalmente fora das deps — evita loop quando pai
    // re-renderiza com o mesmo valor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    hour,
    minute,
    days,
    dom,
    intervalEvery,
    intervalUnit,
    raw,
    runAtInput,
  ])

  const preview =
    mode === 'once'
      ? humanizeDate(runAtInput ? new Date(runAtInput).toISOString() : '')
      : humanizeCron(value.kind === 'cron' ? value.expression : raw)
  const isInvalid =
    preview === 'Expressão cron inválida' || preview === 'Data inválida'

  const toggleDay = (d: number) => {
    const next = new Set(days)
    if (next.has(d)) next.delete(d)
    else next.add(d)
    if (next.size > 0) setDays(next)
  }

  // Select nativo estilizado com tokens do DS (não há Select atom).
  const selectClass = cn(
    'block w-full appearance-none rounded-md border bg-white px-3 py-2.5 text-sm text-gray-900 transition-colors',
    'border-gray-300 hover:border-gray-400',
    'focus-visible:border-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/20',
    'disabled:cursor-not-allowed disabled:opacity-60',
    'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-gray-600'
  )

  return (
    <div className={cn('space-y-5', className)}>
      {/* Modo */}
      <FormField label="Frequência" htmlFor="schedule-mode">
        <select
          id="schedule-mode"
          className={selectClass}
          value={mode}
          onChange={e => setMode(e.target.value as Mode)}
        >
          {MODE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label} — {opt.hint}
            </option>
          ))}
        </select>
      </FormField>

      {/* Horário (modos com HH:MM) */}
      {(mode === 'daily' ||
        mode === 'weekdays' ||
        mode === 'custom_days' ||
        mode === 'monthly') && (
        <FormField
          label="Horário"
          htmlFor="schedule-time"
          hint="Fuso America/Sao_Paulo"
        >
          <Input
            id="schedule-time"
            type="time"
            leftIcon={<Icon name="clock" size={16} />}
            value={formatTime(hour, minute)}
            onChange={e => {
              const { hour: h, minute: m } = parseTimeInput(e.target.value)
              setHour(h)
              setMinute(m)
            }}
          />
        </FormField>
      )}

      {/* Dias específicos */}
      {mode === 'custom_days' && (
        <div>
          <Label>Dias da semana</Label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {WEEKDAY_LABELS.map(d => {
              const active = days.has(d.value)
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  aria-pressed={active}
                  className={cn(
                    'h-11 w-11 rounded-lg text-sm font-semibold transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500',
                    active
                      ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  )}
                >
                  {d.label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Mensal (dia do mês) */}
      {mode === 'monthly' && (
        <FormField label="Dia do mês" htmlFor="schedule-dom">
          <Input
            id="schedule-dom"
            type="number"
            min={1}
            max={31}
            leftIcon={<Icon name="calendar-days" size={16} />}
            value={dom}
            onChange={e =>
              setDom(Math.max(1, Math.min(31, Number(e.target.value) || 1)))
            }
          />
        </FormField>
      )}

      {/* Intervalo */}
      {mode === 'interval' && (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="A cada" htmlFor="schedule-every">
            <Input
              id="schedule-every"
              type="number"
              min={1}
              max={intervalUnit === 'min' ? 59 : 23}
              value={intervalEvery}
              onChange={e =>
                setIntervalEvery(Math.max(1, Number(e.target.value) || 1))
              }
            />
          </FormField>
          <FormField label="Unidade" htmlFor="schedule-unit">
            <select
              id="schedule-unit"
              className={selectClass}
              value={intervalUnit}
              onChange={e => setIntervalUnit(e.target.value as IntervalUnit)}
            >
              {INTERVAL_UNIT_OPTIONS.map(u => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      )}

      {/* Data única (one-shot) */}
      {mode === 'once' && (
        <FormField
          label="Data e hora"
          htmlFor="schedule-runat"
          hint="Fuso local do seu computador. O agendamento dispara uma única vez e é desativado após executar."
        >
          <Input
            id="schedule-runat"
            type="datetime-local"
            leftIcon={<Icon name="calendar-days" size={16} />}
            value={runAtInput}
            min={toDatetimeLocal(new Date(Date.now() + 60_000))}
            onChange={e => setRunAtInput(e.target.value)}
          />
        </FormField>
      )}

      {/* Avançado (raw) */}
      {mode === 'raw' && (
        <FormField
          label="Expressão cron POSIX"
          htmlFor="schedule-raw"
          hint={
            <>
              Formato: <code>min hora dia-mês mês dia-sem</code>. Ex:{' '}
              <code>*/15 * * * *</code> (a cada 15 min), <code>0 9 1 * *</code>{' '}
              (dia 1 às 09:00).
            </>
          }
        >
          <Input
            id="schedule-raw"
            type="text"
            placeholder="0 7 * * 1-5"
            className="font-mono"
            value={raw}
            onChange={e => setRaw(e.target.value)}
          />
        </FormField>
      )}

      {/* Preview */}
      <Alert
        variant={isInvalid ? 'error' : 'info'}
        title={isInvalid ? 'Agenda inválida' : 'Resumo'}
      >
        {preview}
      </Alert>
    </div>
  )
}
